import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EmailserviceService } from '../emailservice.service';
import { ConfigService } from '../shared/services/config.service';
import { APP_CONSTANTS, ROLES, YOUTUBE_URL } from './emails';
import { LANDING_FAQS, LANDING_PARTNER_LOGOS, LANDING_PRICING_PLANS, LANDING_TESTIMONIALS, FaqItem, PartnerLogo, PricingPlan, Testimonial } from '../shared/constants/landing-content';
import { CookieConsentService } from '../services/cookie-consent.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule]
})
export class LandingPageComponent implements OnInit {

  isLoginDropdownOpen: boolean = false;
  isPlayingVideo: boolean = false;
  safeYoutubeUrl: SafeResourceUrl | null = null;
  safeYoutubePlayUrl: SafeResourceUrl | null = null;
  demoForm: FormGroup;
  message: string = '';
  showCookieBanner: boolean = false;
  currentPolicyVersion: string = '';
  roles = ROLES;
  email = APP_CONSTANTS.EMAIL;
  phone = APP_CONSTANTS.PHONE;
  examPortalUrl: string = '';
  activeFaqIndex: number | null = 0;
  statusMessage: string = '';
  statusType: 'success' | 'error' | '' = '';

  faqs: FaqItem[] = LANDING_FAQS;
  testimonials: Testimonial[] = LANDING_TESTIMONIALS;
  pricingPlans: PricingPlan[] = LANDING_PRICING_PLANS;
  partnerLogos: PartnerLogo[] = LANDING_PARTNER_LOGOS;
  partnerLogosRow1: PartnerLogo[] = LANDING_PARTNER_LOGOS.slice(0, 14);
  partnerLogosRow2: PartnerLogo[] = LANDING_PARTNER_LOGOS.slice(14);

  static noRepeatingDigits(control: any) {
    if (!control.value) return null;
    return /^(\d)\1{9}$/.test(control.value) ? { invalidPattern: true } : null;
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  toggleLoginDropdown(): void {
    this.isLoginDropdownOpen = !this.isLoginDropdownOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.isLoginDropdownOpen && !target.closest('.login-dropdown-wrapper')) {
      this.isLoginDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  toggleFaq(index: number): void {
    if (this.activeFaqIndex === index) {
      this.activeFaqIndex = null;
    } else {
      this.activeFaqIndex = index;
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const stage = document.querySelector('.solution-visual-stage');
    if (stage) {
      const rect = stage.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;

      if (rect.top <= windowHeight && rect.bottom >= 0) {
        const scrollOffset = (windowHeight - rect.top) * 0.05;
        const chipAlerts = document.querySelector('.chip-alerts') as HTMLElement;
        const chipCandidates = document.querySelector('.chip-candidates') as HTMLElement;
        const gaugeCard = document.querySelector('.floating-gauge-card') as HTMLElement;

        if (chipAlerts) {
          chipAlerts.style.transform = `translateY(calc(-50% - ${scrollOffset * 0.4}px))`;
        }
        if (chipCandidates) {
          chipCandidates.style.transform = `translateY(-${scrollOffset * 0.7}px)`;
        }
        if (gaugeCard) {
          gaugeCard.style.transform = `translateY(calc(-50% + ${scrollOffset * 0.5}px))`;
        }
      }
    }

    // Bento grid scroll reveal stagger
    const bentoCards = document.querySelectorAll('.bento-left-animate, .bento-right-animate');
    const windowH = window.innerHeight || document.documentElement.clientHeight;
    bentoCards.forEach((card: any) => {
      const r = card.getBoundingClientRect();
      if (r.top < windowH - 60) {
        card.style.opacity = '1';
        card.style.transform = 'translate(0, 0)';
      }
    });
  }

  ngOnInit(): void {
    if (this.config && (this.config.candidatePortalURL || this.config.LandingPageURLCandidate)) {
      this.examPortalUrl = this.config.candidatePortalURL || this.config.LandingPageURLCandidate;
    } else {
      const currentHost = window.location.hostname;
      const examHost = currentHost.startsWith('app') ? currentHost.replace('app', 'exams') : `exams.${currentHost}`;
      this.examPortalUrl = `${window.location.protocol}//${examHost}/`;
    }

    this.initCounterObserver();
    this.initVideoUrls();

    setTimeout(() => {
      this.cookieConsentService.getPolicyVersion().subscribe(version => {
        this.currentPolicyVersion = version;
        this.cookieConsentService.checkConsentStatus(version).subscribe(hasConsented => {
          if (!hasConsented) {
            this.showCookieBanner = true;
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          }
        });
      });
    }, 3000);
  }

  initCounterObserver(): void {
    const statsSection = document.getElementById('heroStatsSection');
    if (!statsSection) return;

    let hasAnimated = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasAnimated) {
          hasAnimated = true;
          this.animateCounters();
        }
      });
    }, { threshold: 0.3 });

    observer.observe(statsSection);
  }

  animateCounters(): void {
    const counters = document.querySelectorAll('.counter-num');
    counters.forEach(counter => {
      const targetAttr = counter.getAttribute('data-target');
      if (!targetAttr) return;

      const target = parseFloat(targetAttr);
      const isFloat = targetAttr.includes('.');
      const suffix = counter.textContent?.replace(/[0-9.]/g, '') || '';

      let start = 0;
      const duration = 2000;
      const startTime = performance.now();

      const updateNumber = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const currentVal = start + (target - start) * progress;

        counter.textContent = (isFloat ? currentVal.toFixed(1) : Math.floor(currentVal).toString()) + suffix;

        if (progress < 1) {
          requestAnimationFrame(updateNumber);
        } else {
          counter.textContent = (isFloat ? target.toFixed(1) : target.toString()) + suffix;
        }
      };

      requestAnimationFrame(updateNumber);
    });
  }

  initVideoUrls(): void {
    const defaultUrl = YOUTUBE_URL || 'https://www.youtube.com/watch?v=DCfjUGOQJRU';
    this.updateVideoUrl(defaultUrl);

    this.service.getLandingConfig().subscribe({
      next: (res: any) => {
        const body = res?.responseBody || res?.body || res?.data || res;
        const urlFromBackend = body?.YOUTUBE_URL || body?.youtubeUrl || body?.youtube_url || body?.youtubeURL || body?.youtubeLink || body?.youtube_link || body?.videoUrl || body?.video_url || body?.url;
        if (urlFromBackend) {
          this.updateVideoUrl(urlFromBackend);
        }
      },
      error: () => {
        this.updateVideoUrl(defaultUrl);
      }
    });
  }

  formatYoutubeEmbedUrl(rawUrl: string): string {
    if (!rawUrl) return '';
    const match = rawUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))([\w-]{11})/);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return rawUrl;
  }

  updateVideoUrl(rawUrl: string): void {
    if (!rawUrl) return;
    const embedUrl = this.formatYoutubeEmbedUrl(rawUrl);
    const baseUrl = embedUrl.split('?')[0];
    const previewUrl = `${baseUrl}?controls=1&rel=0`;
    const playUrl = `${baseUrl}?autoplay=1&controls=1&rel=0`;

    this.safeYoutubeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
    this.safeYoutubePlayUrl = this.sanitizer.bypassSecurityTrustResourceUrl(playUrl);
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  playVideo(): void {
    this.isPlayingVideo = true;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  closeVideo(): void {
    this.isPlayingVideo = false;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  acceptCookies(): void {
    this.showCookieBanner = false;
    this.cookieConsentService.saveConsent('accepted', this.currentPolicyVersion || 'v1.0').subscribe();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  rejectCookies(): void {
    this.showCookieBanner = false;
    this.cookieConsentService.saveConsent('rejected', this.currentPolicyVersion || 'v1.0').subscribe();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  isSubscriptionModalOpen: boolean = false;
  selectedSubscriptionPlan: string = '';
  subscriptionForm: FormGroup;
  subscriptionStatusMessage: string = '';
  subscriptionStatusType: 'success' | 'error' | '' = '';
  isSubmittingSubscription: boolean = false;

  constructor(
    private fb: FormBuilder,
    private route: Router,
    private service: EmailserviceService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private config: ConfigService,
    private cookieConsentService: CookieConsentService
  ) {
    this.demoForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2), Validators.pattern(/^(?!.*\s{2})[A-Za-z][A-Za-z.\- ]*[A-Za-z]$/)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^(\+91[\s-]?)?[6-9]\d{9}$/), LandingPageComponent.noRepeatingDigits]],
      email: ['', [Validators.required, Validators.email]],
      company: ['', Validators.required],
      role: ['', Validators.required],
      dateTime: ['', Validators.required]
    });

    this.subscriptionForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^(\+91[\s-]?)?[6-9]\d{9}$/)]],
      company: [''],
      planName: ['', Validators.required],
      message: ['']
    });
  }

  adminportal() {
    const adminBaseUrl = this.config?.LandingPageURL ? this.config.LandingPageURL.replace(/\/$/, '') : `${window.location.protocol}//${window.location.host}`;
    window.open(`${adminBaseUrl}/account/login`, '_self');
  }

  candidateportal() {
    const targetUrl = this.config?.LandingPageURLCandidate || this.config?.candidatePortalURL || this.examPortalUrl;
    if (targetUrl) {
      window.open(targetUrl, '_blank');
    }
  }

  openSubscriptionModal(planName: string): void {
    this.selectedSubscriptionPlan = planName || 'Standard Plan';
    this.subscriptionForm.patchValue({ planName: this.selectedSubscriptionPlan });
    this.isSubscriptionModalOpen = true;
    this.subscriptionStatusMessage = '';
    this.subscriptionStatusType = '';
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  closeSubscriptionModal(): void {
    this.isSubscriptionModalOpen = false;
    this.subscriptionForm.reset();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  submitSubscriptionForm(): void {
    if (this.subscriptionForm.invalid) {
      this.subscriptionForm.markAllAsTouched();
      return;
    }

    this.isSubmittingSubscription = true;
    this.subscriptionStatusMessage = '';
    this.subscriptionStatusType = '';

    const payload = {
      fullName: this.subscriptionForm.value.fullName,
      email: this.subscriptionForm.value.email,
      phone: this.subscriptionForm.value.phoneNumber,
      company: this.subscriptionForm.value.company || 'Not Specified',
      planName: this.subscriptionForm.value.planName || this.selectedSubscriptionPlan,
      message: this.subscriptionForm.value.message || 'Interested in subscription plan details'
    };

    this.service.sendSubscriptionPlan(payload).subscribe({
      next: () => {
        this.isSubmittingSubscription = false;
        this.subscriptionStatusType = 'success';
        this.subscriptionStatusMessage = `Thank you! Your inquiry for the ${payload.planName} plan has been sent to Sales@certiplate.com. Our team will contact you shortly.`;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        setTimeout(() => {
          this.closeSubscriptionModal();
        }, 3500);
      },
      error: () => {
        this.isSubmittingSubscription = false;
        this.subscriptionStatusType = 'error';
        this.subscriptionStatusMessage = 'Failed to send subscription request. Please try again or email Sales@certiplate.com directly.';
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    });
  }

  openExamPortal() {
    this.candidateportal();
  }

  navigateToBookDemo() {
    this.route.navigate(['/book-demo']);
  }

  navigateToContactUs() {
    this.route.navigate(['/contact-us']);
  }

  scrollToDemoForm() {
    this.route.navigate(['/book-demo']);
  }
  isSubmitting: boolean = false;

  onSubmit() {
    const name = this.demoForm.value.fullName || '';
    const phone = this.demoForm.value.phoneNumber || '';

    this.demoForm.patchValue({
      fullName: name.trim().replace(/\s+/g, ' '),
      phoneNumber: phone.trim()
    });
    if (this.demoForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;

      const formData = {
        type: 'demo',
        ...this.demoForm.value
      };

      this.service.sendDemoRequest(formData).subscribe({
        next: () => {
          this.statusMessage = 'Demo request submitted successfully! We will contact you soon.';
          this.statusType = 'success';
          this.cdr.detectChanges();

          setTimeout(() => {
            this.demoForm.reset();
            this.isSubmitting = false;
            this.cdr.detectChanges();
          }, 100);
        },
        error: (error) => {
          this.statusMessage = 'Error submitting form. Please try again.';
          this.statusType = 'error';
          this.isSubmitting = false;
          this.cdr.detectChanges();
        }
      });
    } else {
      this.demoForm.markAllAsTouched();
    }
  }
}
